import React, { useEffect } from "react";
import { Cell, Row } from ".";
import { TextField } from "@material-ui/core";
import { debounce } from "lodash";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";
import { toParticipantUiText } from "../../../../app/utils/participantTerminology";
import { INITIAL_RISK_WIDTHS } from "./table.utils";

export default function TableRow({
  data,
  readOnly = false,
}) {
  const [initialRender, setInitialRender] = React.useState(true);
  const { setInitialRiskAssessments, setGeneralChangeLog } = useScheduleEvents();
  const [localUpdates, setLocalUpdates] = React.useState({
    ...data,
  });

  const debouncedUpdate = React.useCallback(
    debounce((data) => {
      setInitialRiskAssessments((prev) => {
        const updated = prev.map((item) => {
          if (
            item.scheduleOfEventsRiskAssessmentId ===
            data.scheduleOfEventsRiskAssessmentId
          ) {
            return data;
          }
          return item;
        });
        return updated;
      });

      setGeneralChangeLog((prev) => ({
        ...prev,
        initialRiskAssessments: {
          type: "update",
          table: "initialRiskAssessments",
        },
      }));
    }, 500),
    []
  );

  useEffect(() => {
    if (!initialRender) {
      debouncedUpdate(localUpdates);
    } else {
      setInitialRender(false);
    }
    // eslint-disable-next-line
  }, [localUpdates, debouncedUpdate]);

  return (
    <Row>
      <Cell
        align="flex-start"
        justify="flex-start"
        textAlign="left"
        style={{ width: INITIAL_RISK_WIDTHS.category, textAlign: "left" }}
      >
        {toParticipantUiText(localUpdates.riskAssessmentCategory)}
      </Cell>
      <Cell
        align="flex-start"
        justify="flex-start"
        textAlign="left"
        style={{ width: INITIAL_RISK_WIDTHS.studySpecifics, textAlign: "left" }}
      >
        <CustomTooltip title={localUpdates.riskAssessmentStudySpecifics ?? ""}>
          <div style={{ width: "100%" }}>
            <TextField
              multiline
              variant="outlined"
              value={localUpdates.riskAssessmentStudySpecifics ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setLocalUpdates((prev) => ({
                  ...prev,
                  riskAssessmentStudySpecifics: value,
                }));
              }}
              style={{ width: "100%", maxHeight: "100%" }}
              size="small"
              rows="4"
              inputProps={{ maxLength: 500 }}
              disabled={readOnly}
            />
          </div>
        </CustomTooltip>
      </Cell>
      <Cell
        align="flex-start"
        justify="flex-start"
        textAlign="left"
        style={{ width: INITIAL_RISK_WIDTHS.riskScore, textAlign: "left" }}
      >
        <CustomTooltip title={localUpdates.riskAssessmentRiskScore ?? ""}>
          <div style={{ width: "100%" }}>
            <TextField
              variant="outlined"
              value={localUpdates.riskAssessmentRiskScore ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                const regex = /^$|^(?:0(?:\.\d)?|1(?:\.[0-9]|\.0)?|2(?:\.[0-9])?|3(?:\.0)?)$/;
                if (!regex.test(value) || value >= 100) return;

                setLocalUpdates((prev) => ({
                  ...prev,
                  riskAssessmentRiskScore: value,
                }));
              }}
              style={{ width: "100%" }}
              size="small"
              type="number"
              disabled={readOnly}
            />
          </div>
        </CustomTooltip>
      </Cell>
      <Cell
        align="flex-start"
        justify="flex-start"
        textAlign="left"
        style={{
          width: INITIAL_RISK_WIDTHS.low,
          whiteSpace: "pre-line",
          textAlign: "left",
        }}
      >
        {toParticipantUiText(localUpdates.riskAssessmentLow?.trim())}
      </Cell>
      <Cell
        align="flex-start"
        justify="flex-start"
        textAlign="left"
        style={{
          width: INITIAL_RISK_WIDTHS.moderate,
          whiteSpace: "pre-line",
          textAlign: "left",
        }}
      >
        {toParticipantUiText(localUpdates.riskAssessmentModerate?.trim())}
      </Cell>
      <Cell
        align="flex-start"
        justify="flex-start"
        textAlign="left"
        style={{
          width: INITIAL_RISK_WIDTHS.high,
          whiteSpace: "pre-line",
          textAlign: "left",
        }}
      >
        {toParticipantUiText(localUpdates.riskAssessmentHigh?.trim())}
      </Cell>
    </Row>
  );
}
